import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Grid,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  Text,
} from '@chakra-ui/react';
import type { MultiFileUploadV2FileItem as FileItem } from 'shared-components';
import { Modal, MultiFileUploadV2, FILE_STATUS } from 'shared-components';
import { useSession } from 'shared-components/authentication';
import { useParams } from 'react-router-dom';
import { DocumentType } from 'types';
import { toast } from 'react-toastify';
import { uploadApplicantFileWithProgress, UploadFnParams, UploadResult } from '@/api/api';

const acceptedFileTypes = [
  '.pdf',
  '.jpg',
  '.jpeg',
  '.png',
  '.heic',
  '.heif',
  '.webp',
  '.tiff',
  '.tif',
  '.bmp',
  '.gif',
  '.svg',
];

export type UploadFn = (params: UploadFnParams) => Promise<UploadResult>;

export type DocumentConfig = {
  docType?: DocumentType | null;
  docKey?: string | null;
  label?: string;
  acceptedFileTypes?: string[];
  maxFileSize?: number;
  maxFiles?: number;
};

type Props = {
  id: string;
  title?: string;
  isOpen: boolean;
  onClose: () => void;
  onProceed: (files: FileItem[], deletedS3Keys?: string[]) => void;
  onDeleteExistingFile?: (s3Key: string) => Promise<void>;
  uploadedSuccessfully: boolean;
  fileUploadError: string | null;
  deletedSuccessfully: boolean;
  uploadFn?: UploadFn;
  documentConfig?: DocumentConfig;
  existingFiles?: {
    urls?: string[];
    sKeys?: string[];
    s3Keys?: string[];
  };
};

export const MultiFileUploadModal = ({
  id,
  title = 'Upload files',
  isOpen,
  onClose,
  onProceed,
  onDeleteExistingFile,
  uploadedSuccessfully,
  fileUploadError,
  deletedSuccessfully,
  uploadFn = uploadApplicantFileWithProgress,
  documentConfig,
  existingFiles,
}: Props) => {
  const params = useParams();

  type UploadFileItem = FileItem & {
    url?: string;
    s3Key?: string;
    uploadedFileName?: string;
    isExistingFile?: boolean;
  };
  const [files, setFiles] = useState<UploadFileItem[]>([]);
  const [deletedExistingFiles, setDeletedExistingFiles] = useState<string[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<{ s3Key: string; fileName: string } | null>(null);
  const [lastDeletedFileName, setLastDeletedFileName] = useState<string | null>(null);
  const abortControllers = useRef<Map<string, AbortController>>(new Map());
  const cancelRef = useRef<HTMLButtonElement>(null);
  const deleteConfirmRef = useRef<HTMLButtonElement>(null);

  const sessionContext = useSession();

  const hasUploadingFiles = files.some(file => file.status === FILE_STATUS.UPLOADING);

  const uploadedFiles = useMemo(() => {
    return files.filter(file => file.status === FILE_STATUS.UPLOADED);
  }, [files]);

  const hasNewFiles = uploadedFiles.length > 0;
  const isDisabled = hasUploadingFiles || isProcessing || !hasNewFiles;

  const existingFileItems = useMemo(() => {
    if (!existingFiles?.s3Keys) return [];
    return existingFiles.s3Keys
      .filter(s3Key => !deletedExistingFiles.includes(s3Key))
      .map((s3Key, index) => ({
        id: `existing-${s3Key}`,
        name: s3Key.split('/').pop() || s3Key,
        originalName: s3Key.split('/').pop() || s3Key,
        status: FILE_STATUS.UPLOADED,
        isUpload: true,
        progress: 100,
        size: 0,
        type: '',
        url: existingFiles.urls?.[index],
        s3Key,
        isExistingFile: true,
      }));
  }, [existingFiles, deletedExistingFiles]);

  const allFiles = useMemo(() => {
    return [...existingFileItems, ...files];
  }, [existingFileItems, files]);

  useEffect(() => {
    if (isOpen) {
      setFiles([]);
      setDeletedExistingFiles([]);
    } else {
      setShowDeleteConfirm(false);
      setFileToDelete(null);
      setLastDeletedFileName(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (lastDeletedFileName) {
      if (deletedSuccessfully) {
        toast.success(`File ${lastDeletedFileName} deleted successfully`);

        // Add deleted file to the deletedExistingFiles array to remove it from display
        const deletedFileItem = existingFileItems.find(item => item.name === lastDeletedFileName);
        if (deletedFileItem) {
          setDeletedExistingFiles(prev => [...prev, deletedFileItem.s3Key]);
        }

        setLastDeletedFileName(null);

        // If there are no more files to show and no files being uploaded, close the modal
        const remainingFiles = existingFileItems.filter(item => item.name !== lastDeletedFileName);
        const hasNoFiles = remainingFiles.length === 0 && files.length === 0;
        if (hasNoFiles && !hasUploadingFiles) {
          setTimeout(() => onClose(), 500); // Small delay to let user see the success message
        }
      } else if (fileUploadError) {
        toast.error(`Failed to delete file ${lastDeletedFileName}: ${fileUploadError}`);
        setLastDeletedFileName(null);
      }
    }
  }, [
    deletedSuccessfully,
    fileUploadError,
    lastDeletedFileName,
    existingFileItems,
    files.length,
    hasUploadingFiles,
    onClose,
  ]);

  const uploadToS3 = useCallback(
    async (file: File, fileItem: UploadFileItem) => {
      try {
        const idToken = await sessionContext.getIdToken();
        if (!idToken) {
          throw new Error('Authentication required');
        }

        const { userId, uni } = params;
        const abortController = new AbortController();
        abortControllers.current.set(fileItem.id, abortController);

        const {
          url: fileUrl,
          s3Key,
          fileName: uploadedFileName,
        } = await uploadFn({
          file,
          idToken,
          university: uni,
          applicantId: userId,
          uploadConf: {
            fileName: file.name,
            fileType: file.type,
            docKey: documentConfig?.docKey || null,
            docType: documentConfig?.docType || null,
          },
          onProgress: progress => {
            setFiles(prev => prev.map(f => (f.id === fileItem.id ? { ...f, progress } : f)));
          },
          signal: abortController.signal,
        });

        setFiles(prev =>
          prev.map(f =>
            f.id === fileItem.id
              ? {
                  ...f,
                  status: FILE_STATUS.UPLOADED,
                  progress: 100,
                  uploadEndTime: Date.now(),
                  url: fileUrl,
                  s3Key,
                  uploadedFileName,
                  name: uploadedFileName,
                  originalName: uploadedFileName,
                }
              : f,
          ),
        );

        abortControllers.current.delete(fileItem.id);
      } catch (error) {
        console.error('Upload failed:', error);

        setFiles(prev =>
          prev.map(f =>
            f.id === fileItem.id
              ? {
                  ...f,
                  status: FILE_STATUS.ERROR,
                  error: error instanceof Error ? error.message : 'Upload failed',
                  progress: 0,
                }
              : f,
          ),
        );

        abortControllers.current.delete(fileItem.id);
      }
    },
    [params, sessionContext, documentConfig, uploadFn],
  );

  const handleFileSelect = useCallback(
    (newFiles: File[]) => {
      const newItems: UploadFileItem[] = newFiles.map((file, index) => {
        const id = `file-${Date.now()}-${index}`;
        return {
          id,
          name: file.name,
          originalName: file.name,
          file,
          status: FILE_STATUS.UPLOADING,
          progress: 0,
          size: file.size,
          type: file.type,
          uploadStartTime: Date.now(),
        };
      });

      setFiles(prev => [...prev, ...newItems]);

      newItems.forEach(fileItem => {
        if (fileItem.file) {
          uploadToS3(fileItem.file, fileItem);
        }
      });
    },
    [uploadToS3],
  );

  const handleFileRemove = useCallback(
    async (id: string | number) => {
      const fileId = id.toString();

      if (fileId.startsWith('existing-')) {
        const s3Key = fileId.replace('existing-', '');
        const fileName = s3Key.split('/').pop() || s3Key;

        setFileToDelete({ s3Key, fileName });
        setShowDeleteConfirm(true);
      } else {
        const abortController = abortControllers.current.get(fileId);
        if (abortController) {
          abortController.abort();
          abortControllers.current.delete(fileId);
        }

        setFiles(prev => prev.filter(f => f.id !== id));
      }
    },
    [existingFiles?.s3Keys],
  );

  const handleRetry = useCallback(
    (id: string | number) => {
      const file = files.find(f => f.id === id);
      if (!file || !file.file) return;

      setFiles(prev =>
        prev.map(f =>
          f.id === id
            ? {
                ...f,
                status: FILE_STATUS.READY,
                progress: 0,
                error: undefined,
                retryCount: (f.retryCount || 0) + 1,
              }
            : f,
        ),
      );

      setTimeout(() => {
        uploadToS3(file.file!, file);
      }, 100);
    },
    [files, uploadToS3],
  );

  const handleProceed = useCallback(() => {
    const failedFiles = files.filter(file => file.status === FILE_STATUS.ERROR);

    if (failedFiles.length > 0) {
      toast.error(`${failedFiles.length} file(s) failed to upload. Please retry or remove them.`);
      return;
    }

    // Combine existing (not deleted) and newly uploaded files so consumers receive the full set
    const combinedFiles = [...existingFileItems, ...uploadedFiles];

    if (combinedFiles.length === 0) {
      toast.error('No files to process. Please upload at least one file.');
      return;
    }

    setIsProcessing(true);
    onProceed(uploadedFiles, deletedExistingFiles);
  }, [files, uploadedFiles, existingFileItems, deletedExistingFiles, onProceed]);

  const handleClose = useCallback(() => {
    if (hasUploadingFiles) {
      return;
    }

    if (uploadedFiles.length > 0) {
      setShowCloseConfirm(true);
      return;
    }

    onClose();
  }, [hasUploadingFiles, onClose, uploadedFiles]);

  const handleConfirmClose = useCallback(() => {
    setShowCloseConfirm(false);
    onClose();
  }, [onClose]);

  const handleCancelClose = useCallback(() => {
    setShowCloseConfirm(false);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!fileToDelete) return;

    const { s3Key, fileName } = fileToDelete;

    if (onDeleteExistingFile) {
      try {
        setLastDeletedFileName(fileName);
        await onDeleteExistingFile(s3Key);
      } catch (error) {
        console.error('Error deleting file:', error);
      }
    } else {
      setIsProcessing(true);
      onProceed([], [s3Key]);
    }

    setShowDeleteConfirm(false);
    setFileToDelete(null);
  }, [fileToDelete, onDeleteExistingFile, onProceed]);

  const handleCancelDelete = useCallback(() => {
    setShowDeleteConfirm(false);
    setFileToDelete(null);
  }, []);

  useEffect(() => {
    if (!isProcessing) return;

    if (uploadedSuccessfully && !fileUploadError) {
      const noRemainingFiles = existingFileItems.length === 0;
      if (uploadedFiles.length > 0 || noRemainingFiles) {
        toast.success(`Successfully processed ${uploadedFiles.length} file(s)`);
        setIsProcessing(false);
        onClose();
      } else {
        setIsProcessing(false);
      }
    } else if (fileUploadError) {
      toast.error(fileUploadError);
      setIsProcessing(false);
    }
  }, [uploadedSuccessfully, fileUploadError, isProcessing, uploadedFiles.length, onClose, existingFileItems.length]);

  useEffect(() => {
    if (!isOpen) {
      setIsProcessing(false);
      setFiles([]);
      setShowCloseConfirm(false);
      abortControllers.current.forEach(controller => {
        controller.abort();
      });
      abortControllers.current.clear();
    }
  }, [isOpen]);

  return (
    <>
      <Modal id={id} show={isOpen} title={title} onClose={handleClose} size="lg">
        <Grid rowGap={6}>
          <Box>
            <MultiFileUploadV2
              id={id}
              label={documentConfig?.label || 'Upload file'}
              files={allFiles}
              onFileSelect={handleFileSelect}
              onFileRemove={handleFileRemove}
              onRetry={handleRetry}
              onFilesValidated={(_validFiles, invalidFiles) => {
                if (!invalidFiles || invalidFiles.length === 0) return;

                // Check if there are any max files exceeded errors
                const hasMaxFilesError = invalidFiles.some(
                  ({ error }) => error?.toLowerCase().includes('maximum') && error?.toLowerCase().includes('files'),
                );

                // Show a single toast for max files exceeded
                if (hasMaxFilesError) {
                  toast.error(`Maximum ${documentConfig?.maxFiles || 20} files allowed`);
                }

                // Show individual toasts for other validation errors (size, type, etc.)
                invalidFiles.forEach(({ file, error }) => {
                  if (error) {
                    const isMaxFilesError =
                      error.toLowerCase().includes('maximum') && error.toLowerCase().includes('files');
                    if (!isMaxFilesError) {
                      toast.error(`${file.name}: ${error}`);
                    }
                  }
                });
              }}
              maxFiles={documentConfig?.maxFiles || 20}
              maxFileSize={documentConfig?.maxFileSize || 10 * 1024 * 1024} // 10MB
              acceptedFileTypes={documentConfig?.acceptedFileTypes || acceptedFileTypes}
              isOptional={false}
              allowRetry={true}
              showFileSize={true}
            />
            <Text fontSize="md" color="muted">
              We accept any image or PDF file up to 10MB per file. Please ensure your image is not blurry or out of
              focus, that the text is clearly legible, and the entire document is within the frame.
            </Text>
          </Box>

          <Box display="flex" justifyContent="flex-end" gap={4} mt={2}>
            <Button variant={'secondary'} onClick={handleClose} isDisabled={hasUploadingFiles}>
              Cancel
            </Button>
            <Button
              onClick={handleProceed}
              variant={'solid'}
              isDisabled={isDisabled}
              isLoading={isProcessing}
              loadingText="Processing..."
            >
              Proceed
            </Button>
          </Box>
        </Grid>
      </Modal>

      <AlertDialog isOpen={showCloseConfirm} leastDestructiveRef={cancelRef} onClose={handleCancelClose} isCentered>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Close Modal
            </AlertDialogHeader>

            <AlertDialogBody>Uploaded files will be reset. Do you want to close?</AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={handleCancelClose}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={handleConfirmClose} ml={3}>
                Close
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      <AlertDialog
        isOpen={showDeleteConfirm}
        leastDestructiveRef={deleteConfirmRef}
        onClose={handleCancelDelete}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete File
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to permanently delete "{fileToDelete?.fileName}"? This action cannot be undone.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={deleteConfirmRef} onClick={handleCancelDelete}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={handleConfirmDelete} ml={3}>
                Delete Permanently
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};
